import { useState } from "react";
import { useTranslation } from "react-i18next";
import { trackFormSubmit } from "../../analytics";
import { motion } from "motion/react";
import type { CheckedState } from "@radix-ui/react-checkbox";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";

export type ContactFormProps = {
  onSubmit: (data: {
    name: string;
    email: string;
    phone?: string;
    experience?: string;
    acceptedTerms: boolean;
  }) => Promise<void>;
  initialValues?: Partial<{ 
    name: string; 
    email: string; 
    phone: string; 
    experience: string;
  }>;
  requireAcceptedTerms?: boolean;
  privacyUrl?: string;
};

export function ContactForm({
  onSubmit,
  initialValues = {},
  requireAcceptedTerms = true,
  privacyUrl,
}: ContactFormProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: initialValues.name || "",
    email: initialValues.email || "",
    phone: initialValues.phone || "",
    experience: initialValues.experience || "",
  });
  const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAcceptedTermsChange = (checked: CheckedState) => {
    setAcceptedTerms(checked === true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await onSubmit({
        ...formData,
        acceptedTerms,
      });
      trackFormSubmit('contact_form', 'breeders_contact');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Error al enviar el formulario");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name && formData.email && (!requireAcceptedTerms || acceptedTerms);

  return (
    <section
      id="contacto"
      className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-orange-900 text-white flex items-center py-20"
    >
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="text-center max-w-4xl mx-auto"
        >
          <h2 className="text-5xl md:text-7xl mb-8 text-orange-500">
            {t("breeders.contact.title1")}
            <br />
            <span className="text-white">
              {t("breeders.contact.title2")}
            </span>
          </h2>

          <p className="text-xl md:text-2xl mb-12 text-gray-300">
            {t("breeders.contact.subtitle")}
          </p>

          <Card className="bg-white/10 backdrop-blur-sm border-orange-500/30 p-8 rounded-3xl max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label
                  htmlFor="name"
                  className="text-white text-lg mb-2 block"
                >
                  {t("breeders.contact.form.name")}
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t("breeders.contact.form.placeholders.name")}
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60 rounded-xl"
                  required
                  aria-invalid={!formData.name}
                  aria-describedby={!formData.name ? "name-error" : undefined}
                />
                {!formData.name && (
                  <p id="name-error" className="text-red-300 text-sm mt-1">
                    {t("common.validation.required")}
                  </p>
                )}
              </div>

              <div>
                <Label
                  htmlFor="email"
                  className="text-white text-lg mb-2 block"
                >
                  {t("breeders.contact.form.email")}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t("breeders.contact.form.placeholders.email")}
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60 rounded-xl"
                  required
                  aria-invalid={!formData.email}
                  aria-describedby={!formData.email ? "email-error" : undefined}
                />
                {!formData.email && (
                  <p id="email-error" className="text-red-300 text-sm mt-1">
                    {t("common.validation.required")}
                  </p>
                )}
              </div>

              <div>
                <Label
                  htmlFor="phone"
                  className="text-white text-lg mb-2 block"
                >
                  {t("breeders.contact.form.phone")}
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={t("breeders.contact.form.placeholders.phone")}
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60 rounded-xl"
                />
              </div>

              <div>
                <Label
                  htmlFor="experience"
                  className="text-white text-lg mb-2 block"
                >
                  {t("breeders.contact.form.experience")}
                </Label>
                <Textarea
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                  placeholder={t("breeders.contact.form.placeholders.experience")}
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60 rounded-xl min-h-[100px]"
                />
              </div>

              {requireAcceptedTerms && (
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="terms"
                    checked={acceptedTerms}
                    onCheckedChange={handleAcceptedTermsChange}
                    className="mt-1 bg-white/20 border-white/30 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                    aria-invalid={!acceptedTerms}
                    aria-describedby={!acceptedTerms ? "terms-error" : undefined}
                  />
                  <Label
                    htmlFor="terms"
                    className="text-white text-sm leading-relaxed cursor-pointer"
                  >
                    {t("breeders.contact.form.terms")}
                    {privacyUrl && (
                      <a
                        href={privacyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-400 hover:text-orange-300 underline ml-1"
                      >
                        {t("common.links.privacy")}
                      </a>
                    )}
                  </Label>
                </div>
              )}

              {submitError && (
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
                  <p className="text-red-300 text-sm">{submitError}</p>
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                disabled={!isFormValid || isSubmitting}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white text-xl py-6 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t("common.loading") : t("breeders.contact.form.button")}
              </Button>
            </form>
          </Card>

          {/* Logo at bottom */}
          <motion.div
            className="mt-16"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <img
              src="/assets/d85ba7a4098672812ab5e992f2ac839653942275.png"
              alt="Rebel Genomics"
              className="w-20 h-20 mx-auto opacity-60"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
